import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar, Users, Store, Plus, ShieldCheck, Ban, BarChart3, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import AdminEventsTab from '../components/admin/AdminEventsTab';
import AdminExhibitorsTab from '../components/admin/AdminExhibitorsTab';
import AdminAttendeesTab from '../components/admin/AdminAttendeesTab';
import AdminStatisticsTab from '../components/admin/AdminStatisticsTab';
import AdminSettingsTab from '../components/admin/AdminSettingsTab';

const AdminDashboard = () => {
  const { user } = useContext(AuthContext);
  const { t } = useTranslation();

  // Core data
  const [events, setEvents] = useState([]);
  const [exhibitors, setExhibitors] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [platformStats, setPlatformStats] = useState(null);
  const [activeTab, setActiveTab] = useState('events');

  // Create event modal (triggered from header, rendered in EventsTab)
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);

  // Shared modal state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToCancel, setUserToCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [eventsRes, exhibitorsRes, buyersRes, statsRes] = await Promise.all([
        axios.get(`${API}/events`),
        axios.get(`${API}/admin/exhibitors`),
        axios.get(`${API}/admin/buyers`),
        axios.get(`${API}/stats/admin`).catch(() => ({ data: null })),
      ]);
      setEvents(eventsRes.data);
      setExhibitors(exhibitorsRes.data);
      setBuyers(buyersRes.data);
      setPlatformStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data', error);
      toast.error('Failed to load dashboard');
    }
  };

  const verifyUser = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/verify`);
      toast.success('User verified successfully!');
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to verify user');
    }
  };

  const deleteUser = async () => {
    if (!userToDelete) return;
    try {
      await axios.delete(`${API}/admin/users/${userToDelete.id}`);
      toast.success('User deleted successfully');
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const cancelRegistration = async () => {
    if (!userToCancel || !userToCancel.eventId) return;
    try {
      const response = await axios.post(
        `${API}/admin/cancel-registration/${userToCancel.id}/${userToCancel.eventId}`,
        { reason: cancelReason }
      );
      toast.success(`Registration cancelled! ${response.data.refund_required ? 'Refund may be required.' : ''}`);
      setShowCancelModal(false);
      setUserToCancel(null);
      setCancelReason('');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel registration');
    }
  };

  const openCancelModal = (userData, eventId) => {
    setUserToCancel({ ...userData, eventId });
    setShowCancelModal(true);
  };

  const handleDeleteUser = (userData) => {
    setUserToDelete(userData);
    setShowDeleteConfirm(true);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-1">{t('dashboard.admin_dashboard')}</h1>
            <p className="text-slate-600">{t('dashboard.welcome')}, {user?.name}</p>
          </div>
          <Button data-testid="create-event-btn" onClick={() => setShowCreateEventModal(true)}>
            <Plus className="mr-2 h-4 w-4" /> {t('dashboard.add_event')}
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">{t('dashboard.events')}</p>
                <p className="text-2xl font-bold text-slate-900">{events.length}</p>
              </div>
              <Calendar className="h-8 w-8 text-primary" />
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">{t('dashboard.exhibitors')}</p>
                <p className="text-2xl font-bold text-slate-900">{exhibitors.length}</p>
              </div>
              <Store className="h-8 w-8 text-accent" />
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">{t('nav.attendees') || 'Attendees'}</p>
                <p className="text-2xl font-bold text-slate-900">{buyers.length}</p>
              </div>
              <Users className="h-8 w-8 text-primary" />
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">{t('common.verified') || 'Verified'}</p>
                <p className="text-2xl font-bold text-green-600">{[...exhibitors, ...buyers].filter(u => u.is_verified).length}</p>
              </div>
              <ShieldCheck className="h-8 w-8 text-green-600" />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-5 max-w-2xl">
            <TabsTrigger value="events">{t('dashboard.events')}</TabsTrigger>
            <TabsTrigger value="exhibitors">{t('dashboard.exhibitors')}</TabsTrigger>
            <TabsTrigger value="buyers">{t('nav.attendees') || 'Attendees'}</TabsTrigger>
            <TabsTrigger value="statistics">
              <BarChart3 className="h-4 w-4 mr-1" /> {t('dashboard.stats')}
            </TabsTrigger>
            <TabsTrigger value="settings">
              <Settings className="h-4 w-4 mr-1" /> {t('dashboard.settings')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="events">
            <AdminEventsTab 
              events={events} 
              fetchDashboardData={fetchDashboardData}
              showCreateModal={showCreateEventModal}
              setShowCreateModal={setShowCreateEventModal}
            />
          </TabsContent>

          <TabsContent value="exhibitors">
            <AdminExhibitorsTab 
              exhibitors={exhibitors} 
              fetchDashboardData={fetchDashboardData}
              verifyUser={verifyUser}
              openCancelModal={openCancelModal}
              onDeleteUser={handleDeleteUser}
            />
          </TabsContent>

          <TabsContent value="buyers">
            <AdminAttendeesTab 
              buyers={buyers}
              verifyUser={verifyUser}
              openCancelModal={openCancelModal}
              onDeleteUser={handleDeleteUser}
            />
          </TabsContent>

          <TabsContent value="statistics">
            <AdminStatisticsTab platformStats={platformStats} />
          </TabsContent>

          <TabsContent value="settings">
            <AdminSettingsTab user={user} fetchDashboardData={fetchDashboardData} />
          </TabsContent>
        </Tabs>

        {/* Shared: Delete User Confirmation */}
        <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-600">Delete User</DialogTitle>
              <DialogDescription>
                Are you sure you want to permanently delete <strong>{userToDelete?.name}</strong>?
              </DialogDescription>
            </DialogHeader>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-sm">
              <p className="text-amber-800">
                <strong>Note:</strong> This will remove the user and all their data permanently. Process refunds separately through PayPal.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Keep</Button>
              <Button variant="destructive" onClick={deleteUser}>Delete User</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Shared: Cancel Registration Modal */}
        <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-amber-600">Cancel Event Registration</DialogTitle>
              <DialogDescription>
                Cancel <strong>{userToCancel?.name}</strong>'s registration. The user account will remain active but their event registration will be removed.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-md p-3 text-sm">
                <p><strong>User:</strong> {userToCancel?.name} ({userToCancel?.email})</p>
                <p><strong>Company:</strong> {userToCancel?.company || 'N/A'}</p>
              </div>
              <div>
                <Label>Cancellation Reason (Optional)</Label>
                <Textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Enter reason for cancellation (this will be sent to the user)"
                  rows={3}
                />
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm">
                <p className="text-blue-800">
                  <strong>Email Notification:</strong> The user will be notified via email about this cancellation.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowCancelModal(false); setCancelReason(''); }}>Keep Registration</Button>
              <Button variant="destructive" onClick={cancelRegistration}>
                <Ban className="h-4 w-4 mr-1" /> Cancel Registration
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminDashboard;
